import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Zap, ArrowLeft, Mail, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Please enter your email'); return; }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('If an account exists, a reset link has been sent.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ background: '#050505' }}>
      <div className="w-full max-w-md">
        <button onClick={() => navigate('/auth')} className="flex items-center gap-2 text-sm mb-8 transition-colors" style={{ color: '#94A3B8' }}>
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: '#6366F1' }}>
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Syne' }}>Regret Engine</span>
        </div>

        {!sent ? (
          <>
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne' }}>Forgot your password?</h1>
            <p className="text-sm mb-8" style={{ color: '#94A3B8' }}>
              No worries. Enter your email and we'll send you a link to reset it.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: '#475569' }}>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#475569' }} />
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required
                    className="pl-10 py-5 rounded-lg text-sm" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#F8FAFC' }} />
                </div>
              </div>

              <Button type="submit" disabled={isLoading}
                className="w-full rounded-lg py-5 text-sm font-semibold transition-transform hover:-translate-y-0.5"
                style={{ background: '#6366F1', boxShadow: '0 0 15px rgba(99,102,241,0.3)' }}>
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(16,185,129,0.1)' }}>
              <CheckCircle className="w-8 h-8" style={{ color: '#10B981' }} />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne' }}>Check your email</h1>
            <p className="text-sm mb-6" style={{ color: '#94A3B8' }}>
              We've sent a password reset link to <strong style={{ color: '#F8FAFC' }}>{email}</strong>. Click the link in the email to set a new password.
            </p>
            <p className="text-xs mb-8" style={{ color: '#475569' }}>
              Didn't receive the email? Check your spam folder, or try again in a few minutes.
            </p>
            <div className="space-y-3">
              <Button onClick={() => { setSent(false); setEmail(''); }} variant="outline"
                className="w-full rounded-lg py-5 text-sm border-white/10 hover:bg-white/5">
                Try a different email
              </Button>
              <Button onClick={() => navigate('/auth')}
                className="w-full rounded-lg py-5 text-sm font-semibold"
                style={{ background: '#6366F1' }}>
                Back to Sign In
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
